import { useState, useEffect } from 'react';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Calendar,
  MessageSquare,
  Trash2,
  Filter,
  History,
  Clock,
  ArrowRight,
  Users,
  Briefcase
} from 'lucide-react';
import { Task, Staff, staffService, taskService } from '@/lib/storage';
import { useToast } from '@/hooks/use-toast';
import { CreateTaskDialog } from '@/components/CreateTaskDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';

const AdminTasksPage = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async (silent = false) => {
    try {
      const allTasks = await taskService.getAll();
      setTasks(allTasks);
      const allStaff = await staffService.getAll();
      setStaffList(allStaff);
    } catch (error) {
      console.error("Failed to load tasks data", error);
      if (!silent) {
        toast({ title: 'Failed to load data', variant: 'destructive' });
      }
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (task.clientName && task.clientName.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStaffName = (staffId: string) => {
    const staff = staffList.find(s => s.id === staffId);
    return staff?.name || 'Unassigned';
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'P0': return 'priority_high';
      case 'P1': return 'priority_medium';
      case 'P2': return 'priority_low';
      default: return 'secondary';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Pending': return 'status_pending';
      case 'In Progress': return 'status_progress';
      case 'Completed': return 'status_completed';
      default: return 'secondary';
    }
  };

  const handleStatusChange = async (e: React.MouseEvent, taskId: string, newStatus: 'Pending' | 'In Progress' | 'Completed') => {
    e.stopPropagation();
    try {
      await taskService.updateStatus(taskId, newStatus, 'admin', 'Administrator');
      toast({ title: `Task status updated to ${newStatus}` });
      loadData();

      if (selectedTask?.id === taskId) {
        const updatedTask = await taskService.getById(taskId);
        if (updatedTask) setSelectedTask(updatedTask);
      }
    } catch (error) {
      toast({ title: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleDelete = async (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this task?')) {
      try {
        await taskService.delete(taskId);
        toast({ title: 'Task deleted' });
        loadData();
        if (selectedTask?.id === taskId) setSelectedTask(null);
      } catch (error) {
        toast({ title: 'Failed to delete task', variant: 'destructive' });
      }
    }
  };

  const sortByDate = (taskList: Task[]) =>
    [...taskList].sort((a, b) => {
      const aDate = a.platforms?.[0]?.startDate || a.deadline || '';
      const bDate = b.platforms?.[0]?.startDate || b.deadline || '';
      return new Date(aDate).getTime() - new Date(bDate).getTime();
    });

  const groupedTasks = {
    'Pending': sortByDate(filteredTasks.filter(t => t.status === 'Pending')),
    'In Progress': sortByDate(filteredTasks.filter(t => t.status === 'In Progress')),
    'Completed': sortByDate(filteredTasks.filter(t => t.status === 'Completed')),
  };

  const getProgress = (task: Task) => {
    if (!task.platforms || task.platforms.length === 0) return null;
    const total = task.platforms.length;
    const done = task.platforms.filter(p => p.status === 'Completed').length;
    return { done, total, pct: Math.round((done / total) * 100) };
  };

  const getPlatformsByDate = (task: Task) => {
    if (!task.platforms) return {};
    const sorted = [...task.platforms].sort((a, b) =>
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    return sorted.reduce((groups: Record<string, typeof sorted>, p) => {
      const key = p.startDate;
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
      return groups;
    }, {});
  };

  return (
    <div className="space-y-6">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Task Board</h2>
          <p className="text-sm text-slate-500">Track and manage campaigns and daily task progress.</p>
        </div>
        <Button
          variant="royal"
          onClick={() => setIsDialogOpen(true)}
          className="rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-primary/5 w-full sm:w-auto"
        >
          <Plus className="w-4.5 h-4.5" /> Create Task
        </Button>
      </div>

      {/* Filters Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
          <Input
            placeholder="Search campaigns, descriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 rounded-xl bg-white/50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-700 transition-all font-medium text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-11 w-full sm:w-[170px] rounded-xl border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-950/40">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <SelectValue placeholder="Status Filter" />
            </div>
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="In Progress">In Progress</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1600px] mx-auto">
        {Object.entries(groupedTasks).map(([status, statusTasks]) => (
          <div key={status} className="space-y-4">
            {/* Column Header */}
            <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100 dark:border-slate-800/50 pb-2">
              <div className="flex items-center gap-2">
                <Badge variant={getStatusVariant(status) as any} className="text-[10px] uppercase font-bold py-0.5 px-2 rounded-md">
                  {status}
                </Badge>
                <span className="text-slate-400 text-xs font-semibold">({statusTasks.length})</span>
              </div>
            </div>

            {/* Kanban Column List */}
            <div className="space-y-3.5 min-h-[400px]">
              <AnimatePresence mode="popLayout">
                {statusTasks.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-8 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-center text-slate-400 text-xs font-medium bg-slate-100/10 dark:bg-slate-900/10"
                  >
                    No {status.toLowerCase()} tasks
                  </motion.div>
                ) : (
                  statusTasks.map((task) => (
                    <motion.div
                      key={task.id}
                      layoutId={`task-card-${task.id}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 350, damping: 25 }}
                      whileHover={{ scale: 1.015 }}
                      className="h-full"
                    >
                      <GlassCard
                        className="cursor-pointer hover:shadow-md transition-shadow border-slate-200/50 dark:border-slate-850 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl flex flex-col justify-between overflow-hidden relative group"
                        onClick={() => setSelectedTask(task)}
                      >
                        <CardContent className="p-5 flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex items-start justify-between gap-2.5 mb-2.5">
                              <h4 className="font-bold text-sm text-slate-850 dark:text-slate-100 leading-snug line-clamp-2">
                                {task.clientName || task.title}
                              </h4>
                              <Badge variant={getPriorityVariant(task.priority) as any} className="shrink-0 text-[9px] font-bold rounded-md px-1.5 h-4.5">
                                {task.priority}
                              </Badge>
                            </div>

                            {task.description && (
                              <p className="text-xs text-slate-500 line-clamp-2 mb-3.5 font-medium leading-relaxed">
                                {task.description}
                              </p>
                            )}

                            {/* Custom progress indicators */}
                            {getProgress(task) && (
                              <div className="space-y-1 mb-4">
                                <div className="flex justify-between items-center text-[10px] text-slate-400 font-semibold">
                                  <span>Execution</span>
                                  <span>{getProgress(task)!.done}/{getProgress(task)!.total} Done</span>
                                </div>
                                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
                                    style={{ width: `${getProgress(task)!.pct}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-slate-450 dark:text-slate-500 text-[11px] font-medium mb-4">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                <span>{new Date(task.deadline).toLocaleDateString()}</span>
                              </div>
                              {task.createdAt && (
                                <div className="flex items-center gap-1 text-[10px] ml-auto">
                                  <Clock className="w-3 h-3 text-slate-400" />
                                  <span>{new Date(task.createdAt).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800/40 pt-3.5 mt-auto" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {status !== 'Pending' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] font-bold rounded-lg px-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                  onClick={(e) => handleStatusChange(e, task.id, 'Pending')}
                                >
                                  Pending
                                </Button>
                              )}
                              {status !== 'In Progress' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] font-bold rounded-lg px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/20"
                                  onClick={(e) => handleStatusChange(e, task.id, 'In Progress')}
                                >
                                  In Progress
                                </Button>
                              )}
                              {status !== 'Completed' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-[10px] font-bold rounded-lg px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/20"
                                  onClick={(e) => handleStatusChange(e, task.id, 'Completed')}
                                >
                                  Complete
                                </Button>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 ml-auto"
                              onClick={(e) => handleDelete(e, task.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {(task.comments.length > 0 || (task.statusHistory && task.statusHistory.length > 0)) && (
                            <div className="flex items-center gap-3 mt-3.5 pt-3 border-t border-slate-100 dark:border-slate-800/40 text-[10px] text-slate-400 font-semibold uppercase tracking-wide">
                              {task.comments.length > 0 && (
                                <span className="flex items-center gap-1.5">
                                  <MessageSquare className="w-3.5 h-3.5" />
                                  {task.comments.length} Update{task.comments.length !== 1 && 's'}
                                </span>
                              )}
                              {task.statusHistory && task.statusHistory.length > 0 && (
                                <span className="flex items-center gap-1.5 ml-auto">
                                  <History className="w-3.5 h-3.5" />
                                  {task.statusHistory.length} Log{task.statusHistory.length !== 1 && 's'}
                                </span>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </GlassCard>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>

      <CreateTaskDialog 
        isOpen={isDialogOpen} 
        onOpenChange={setIsDialogOpen} 
        onSuccess={loadData}
      />

      {/* Task Details Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden p-0">
          <div className="p-6 pb-4 border-b border-slate-150 dark:border-slate-800/50">
            <DialogTitle className="flex flex-col gap-1.5">
              <span className="text-xl font-bold text-slate-900 dark:text-white leading-tight">{selectedTask?.title}</span>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={getPriorityVariant(selectedTask?.priority || 'P1') as any} className="text-[10px] font-bold">
                  Priority {selectedTask?.priority}
                </Badge>
                <Badge variant={getStatusVariant(selectedTask?.status || 'Pending') as any} className="text-[10px] font-bold">
                  Status: {selectedTask?.status}
                </Badge>
              </div>
            </DialogTitle>
            <DialogDescription className="space-y-1.5 mt-3 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-semibold text-slate-600 dark:text-slate-400">Assigned Team:</span>
                <div className="flex flex-wrap gap-1">
                  {selectedTask?.assignedStaff?.map(id => (
                    <Badge key={id} variant="secondary" className="text-[9px] font-bold px-1.5 h-4.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-350 rounded-md border-none">
                      {getStaffName(id)}
                    </Badge>
                  ))}
                  {(!selectedTask?.assignedStaff || selectedTask.assignedStaff.length === 0) && (
                    <span className="text-xs font-semibold text-slate-650">{getStaffName(selectedTask?.assignedTo || '')}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-medium">Deadline Date: <span className="font-bold text-slate-600 dark:text-slate-355">{selectedTask && new Date(selectedTask.deadline).toLocaleDateString()}</span></span>
              </div>
            </DialogDescription>
          </div>

          <ScrollArea className="flex-1 p-6">
            <div className="space-y-6 pb-2">
              {selectedTask?.clientName && (
                <div className="grid grid-cols-2 gap-4 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 text-xs">
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Client</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{selectedTask.clientName}</p>
                    {selectedTask.clientWap && <p className="text-xs text-slate-500 font-semibold">{selectedTask.clientWap}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Campaign</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{selectedTask.campaignName}</p>
                  </div>
                </div>
              )}

              {selectedTask?.platforms && selectedTask.platforms.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Date-wise Platform Checklists</h4>
                  <div className="space-y-3">
                    {Object.entries(getPlatformsByDate(selectedTask)).map(([date, plats]) => (
                      <div key={date} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            {new Date(date + 'T00:00:00').toLocaleDateString(undefined, {
                              weekday: 'short', day: 'numeric', month: 'short'
                            })}
                          </span>
                        </div>
                        <div className="space-y-1.5 ml-4">
                          {(plats as any[]).map((p: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between p-3 border border-slate-150 dark:border-slate-850 rounded-xl bg-card text-xs">
                              <div className="flex items-center gap-2.5">
                                <div className={`w-2 h-2 rounded-full ${p.status === 'Completed' ? 'bg-emerald-500 shadow-sm shadow-emerald-500/20' : p.status === 'In Progress' ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-700'}`} />
                                <span className={`font-semibold ${p.status === 'Completed' ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>
                                  {p.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                {p.amount > 0 && <span className="text-slate-450 dark:text-slate-500 font-semibold">₹{p.amount}/day</span>}
                                {p.times && p.times.length > 0 && (
                                  <div className="flex gap-1">
                                    {p.times.map((t: string, ti: number) => (
                                      <Badge key={ti} variant="outline" className="text-[9px] font-semibold h-4.5 px-1.5 border-slate-200 dark:border-slate-800">
                                        {t}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <Badge variant={getStatusVariant(p.status) as any} className="h-4.5 text-[9px] font-bold rounded-md px-1.5">{p.status}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedTask?.remarks && (
                <div className="space-y-2.5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Remarks & Guidelines</h4>
                  <div className="p-4 bg-slate-55/50 dark:bg-slate-950/20 border border-slate-100 dark:border-slate-850 rounded-2xl text-xs whitespace-pre-wrap leading-relaxed italic text-slate-700 dark:text-slate-300 font-medium">
                    {selectedTask.remarks}
                  </div>
                </div>
              )}

              {/* Updates Timeline */}
              <div className="space-y-3.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-indigo-500" />
                  Staff Submissions ({selectedTask?.comments?.length || 0})
                </h4>
                {selectedTask?.comments && selectedTask.comments.length > 0 ? (
                  <div className="space-y-3">
                    {[...selectedTask.comments].reverse().map((comment, index) => (
                      <div key={index} className="p-3 bg-slate-100/50 dark:bg-slate-950/40 border border-slate-200/20 rounded-2xl space-y-1 text-xs">
                        <div className="flex items-center justify-between font-bold text-slate-700 dark:text-slate-300">
                          <span>{comment.authorName}</span>
                          <span className="text-[10px] text-slate-400 font-medium">
                            {new Date(comment.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-slate-600 dark:text-slate-350 leading-relaxed font-medium">
                          {comment.content}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No submissions or comments logged yet.</p>
                )}
              </div>

              {/* History Timeline */}
              <div className="space-y-3.5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <History className="w-4 h-4 text-indigo-500" />
                  Audit Trail History
                </h4>

                {!selectedTask?.statusHistory?.length ? (
                  <p className="text-xs text-slate-400 italic">No historical status modifications recorded.</p>
                ) : (
                  <div className="space-y-4.5 border-l-2 border-slate-150 dark:border-slate-800 ml-2 pl-4 pt-1">
                    {[...selectedTask.statusHistory].reverse().map((update, index) => (
                      <div key={index} className="relative">
                        <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-primary ring-4 ring-white dark:ring-slate-950" />
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 dark:text-slate-250">{update.updatedByName}</span>
                            <span className="text-slate-500">updated workflow status</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="text-slate-450 dark:text-slate-500 text-[9px] font-semibold py-0">{update.previousStatus}</Badge>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                            <Badge variant={getStatusVariant(update.newStatus) as any} className="text-[9px] font-bold py-0">{update.newStatus}</Badge>
                          </div>
                          <p className="text-[10px] text-slate-400">
                            {new Date(update.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTasksPage;
